import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CommandPalette } from '@/features/search';
import { cn } from '@/shared/lib/cn';

type CommandBarVariant = 'sidebar' | 'rail' | 'phone';

const variantClass: Record<CommandBarVariant, string> = {
  sidebar:
    'border-hairline text-ink-soft hover:text-ink flex h-[42px] w-full items-center gap-2.5 rounded-control border bg-white px-3 text-[13px]',
  rail: 'text-ink hover:bg-card-alt flex size-12 items-center justify-center rounded-lg',
  phone: 'flex size-11 items-center justify-center rounded-pill border border-white/25 text-white',
};

/**
 * Einstieg in die globale Befehlsleiste (Suche, Navigation, „Erweiterte Suche“). Knopf und
 * `Strg/⌘ K` öffnen die Palette; Escape und Fokus-Rückgabe übernimmt die Palette (Radix-Dialog).
 */
export function CommandBar({ variant = 'sidebar' }: { variant?: CommandBarVariant }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(variantClass[variant], 'focus-visible:outline-forest')}
        aria-label="Suche und Aktionen öffnen"
        title={variant === 'sidebar' ? undefined : 'Suchen · Strg K'}
      >
        <Search size={variant === 'sidebar' ? 16 : 19} aria-hidden />
        {variant === 'sidebar' && (
          <>
            <span className="flex-1 text-left">Suchen</span>
            <kbd className="bg-card-alt rounded-sm px-1.5 py-0.5 font-mono text-[11px]">Strg K</kbd>
          </>
        )}
      </button>
      <CommandPalette open={open} onOpenChange={setOpen} />
    </>
  );
}
